import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { formatDateTime, numberID } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, ArrowLeftRight, MoreVertical, X, Eye } from "lucide-react";

const FLOW = ["Draft", "Menunggu Persetujuan", "Dalam Pengiriman", "Diterima", "Dibatalkan"];

export default function TransferStok() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [wh, setWh] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const load = () => { setRows(null); api.get("/inventory/transfers").then((r) => setRows(r.data)); };
  useEffect(() => { api.get("/inventory/warehouses").then((r) => setWh(r.data)); load(); }, []);
  const whName = (id) => wh.find((w) => w.id === id)?.name || id;
  const setStatus = async (t, status) => {
    try { await api.patch(`/inventory/transfers/${t.id}/status`, null, { params: { status } }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <PageHeader title="Transfer Stok" subtitle="Alur: Konveksi (Barang Jadi Produksi) → Inventory Pusat. Stok berpindah saat status Diterima." testId="transfer-header">
        {can("inventory.transfer") && <Button onClick={() => setOpen(true)} data-testid="create-transfer-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Buat Transfer</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada transfer" icon={ArrowLeftRight} /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Deskripsi PO</TableHead><TableHead>Asal</TableHead><TableHead>Tujuan</TableHead><TableHead className="text-right">Item</TableHead><TableHead>Dibuat</TableHead><TableHead>Dibuat Oleh</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id} data-testid={`transfer-row-${t.transfer_no}`}>
                    <TableCell className="font-mono text-xs">{t.transfer_no}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-xs text-slate-600" title={t.po_description || ""} data-testid={`transfer-podesc-${t.transfer_no}`}>{t.po_description || "—"}</TableCell>
                    <TableCell>{whName(t.from_warehouse_id)}</TableCell><TableCell>{whName(t.to_warehouse_id)}</TableCell>
                    <TableCell className="text-right">{t.items.length}</TableCell><TableCell className="text-xs text-slate-500">{formatDateTime(t.created_at)}</TableCell>
                    <TableCell className="text-xs text-slate-600" data-testid={`transfer-createdby-${t.transfer_no}`}>{t.created_by_name || "Tidak tersedia"}</TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setDetailId(t.id)} data-testid={`transfer-detail-${t.transfer_no}`}><Eye className="mr-1 h-4 w-4" />Detail</Button>
                        {can("inventory.transfer") && t.status !== "Diterima" && t.status !== "Dibatalkan" && (
                          <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">{FLOW.filter((s) => s !== t.status).map((s) => <DropdownMenuItem key={s} onClick={() => setStatus(t, s)} data-testid={`transfer-status-${t.transfer_no}-${s}`}>Ubah ke {s}</DropdownMenuItem>)}</DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {open && <TransferDialog wh={wh} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {detailId && <TransferDetail id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function TransferDetail({ id, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/inventory/transfers/${id}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [id]);
  const na = (v) => (v == null || v === "" ? "Tidak tersedia" : v);
  const dt = (v) => (v ? formatDateTime(v) : "Tidak tersedia");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="transfer-detail-modal">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2">Detail Transfer {d ? <span className="font-mono text-sm text-slate-500">{d.transfer_no}</span> : ""}{d && <StatusBadge status={d.status} />}</DialogTitle></DialogHeader>
        {err ? <p className="py-8 text-center text-sm text-slate-400">Gagal memuat detail transfer.</p> : !d ? <Loading /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[["Inventory Asal", na(d.from_warehouse?.name)], ["Inventory Tujuan", na(d.to_warehouse?.name)],
                ["Dibuat Oleh", na(d.created_by_name)], ["Dibuat (WIB)", dt(d.created_at)],
                ["Diterima Oleh", na(d.received_by_name)], ["Diterima (WIB)", dt(d.received_at)],
                ["Pengirim", na(d.pengirim)], ["Penerima", na(d.penerima)]].map(([k, v]) => (
                <div key={k}><p className="text-[11px] uppercase tracking-wider text-slate-400">{k}</p><p className="font-medium text-slate-800" data-testid={`td-${k}`}>{v}</p></div>
              ))}
            </div>
            <div>
              <p className="mb-1 text-sm font-semibold text-slate-700">Deskripsi PO</p>
              <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="td-po-description">{d.po_description && d.po_description.trim() !== "" ? d.po_description : "—"}</p>
            </div>
            <div>
              <p className="mb-1 text-sm font-semibold text-slate-700">Produk Ditransfer</p>
              <Table>
                <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Deskripsi</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.items.map((it, i) => (
                    <TableRow key={i} data-testid={`td-item-${i}`}>
                      <TableCell className="font-mono text-xs">{na(it.sku)}</TableCell>
                      <TableCell className="text-xs text-slate-600">{na(it.description)}</TableCell>
                      <TableCell className="text-right font-medium">{numberID(it.qty)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow><TableCell colSpan={2} className="text-right text-sm font-semibold">Total Qty</TableCell><TableCell className="text-right font-bold" data-testid="td-total-qty">{numberID(d.total_qty)}</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
            {d.notes ? <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="td-notes"><span className="font-semibold">Catatan:</span> {d.notes}</div> : null}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="td-close">Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ wh, onClose, onSaved }) {
  const [form, setForm] = useState({ from_warehouse_id: "", to_warehouse_id: "", pengirim: "", penerima: "", po_description: "", notes: "" });
  const [items, setItems] = useState([{ ref_id: "", qty: 1 }]);
  const [stock, setStock] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form.from_warehouse_id) { setStock([]); return; }
    api.get("/inventory/balances", { params: { warehouse_id: form.from_warehouse_id, ref_type: "variant" } })
      .then((r) => setStock(r.data.filter((b) => b.available > 0))).catch(() => setStock([]));
    setItems([{ ref_id: "", qty: 1 }]);
  }, [form.from_warehouse_id]);

  const availOf = (id) => stock.find((s) => s.ref_id === id)?.available ?? 0;
  const skuOf = (id) => stock.find((s) => s.ref_id === id)?.sku || "";

  const save = async () => {
    if (!form.from_warehouse_id || !form.to_warehouse_id) return toast.error("Pilih gudang asal & tujuan");
    if (items.some((i) => !i.ref_id)) return toast.error("Lengkapi item transfer");
    if ((form.po_description || "").length > 1000) return toast.error("Deskripsi PO maksimal 1.000 karakter");
    for (const i of items) {
      if (Number(i.qty) <= 0) return toast.error("Qty harus > 0");
      if (Number(i.qty) > availOf(i.ref_id)) return toast.error("Jumlah transfer melebihi stok barang jadi yang tersedia.");
    }
    setSaving(true);
    try {
      await api.post("/inventory/transfers", { ...form, items: items.map((i) => ({ ref_type: "variant", ref_id: i.ref_id, qty: Number(i.qty) })) });
      toast.success("Transfer dibuat (Draft)"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Buat Transfer Stok</DialogTitle><DialogDescription>Pilih gudang asal untuk menampilkan barang jadi yang tersedia. Stok berpindah otomatis saat status "Diterima".</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs text-slate-500">Gudang Asal</Label><Select value={form.from_warehouse_id} onValueChange={(v) => setForm({ ...form, from_warehouse_id: v })}><SelectTrigger className="mt-1" data-testid="t-from"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{wh.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Gudang Tujuan</Label><Select value={form.to_warehouse_id} onValueChange={(v) => setForm({ ...form, to_warehouse_id: v })}><SelectTrigger className="mt-1" data-testid="t-to"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{wh.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Pengirim</Label><Input className="mt-1" value={form.pengirim} onChange={(e) => setForm({ ...form, pengirim: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Penerima</Label><Input className="mt-1" value={form.penerima} onChange={(e) => setForm({ ...form, penerima: e.target.value })} /></div>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Deskripsi PO <span className="text-slate-400">(opsional)</span></Label>
          <Textarea className="mt-1" rows={3} maxLength={1000} placeholder="Keterangan manual untuk dokumen transfer ini..." value={form.po_description} onChange={(e) => setForm({ ...form, po_description: e.target.value })} data-testid="t-po-description" />
          <p className="mt-1 text-right text-[11px] text-slate-400">{(form.po_description || "").length}/1000</p>
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between"><Label className="text-sm font-semibold">Item (Barang Jadi Tersedia)</Label><Button size="sm" variant="outline" disabled={!form.from_warehouse_id} onClick={() => setItems([...items, { ref_id: "", qty: 1 }])}><Plus className="mr-1 h-3.5 w-3.5" /> Item</Button></div>
          {!form.from_warehouse_id ? <p className="text-xs text-slate-400">Pilih gudang asal terlebih dahulu.</p> :
            stock.length === 0 ? <p className="text-xs text-slate-400" data-testid="t-empty-stock">Tidak ada barang jadi tersedia di gudang asal ini.</p> :
            items.map((it, i) => (
              <div key={i} className="mb-2 grid grid-cols-12 items-center gap-2">
                <div className="col-span-8"><Select value={it.ref_id} onValueChange={(v) => setItems(items.map((x, idx) => idx === i ? { ...x, ref_id: v } : x))}><SelectTrigger data-testid={`t-item-${i}`}><SelectValue placeholder="Pilih SKU..." /></SelectTrigger><SelectContent>{stock.map((s) => <SelectItem key={s.ref_id} value={s.ref_id}><span className="font-mono text-xs">{s.sku}</span> · tersedia {numberID(s.available)}</SelectItem>)}</SelectContent></Select></div>
                <Input type="number" min="1" className="col-span-3" value={it.qty} onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} data-testid={`t-qty-${i}`} />
                <Button variant="ghost" size="sm" className="col-span-1 text-rose-500" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>
                {it.ref_id && Number(it.qty) > availOf(it.ref_id) && <p className="col-span-12 text-[11px] text-rose-600">Melebihi stok tersedia {skuOf(it.ref_id)} ({numberID(availOf(it.ref_id))}).</p>}
              </div>
            ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-transfer-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
